/**
 * Coordinate system utilities for Cairn
 *
 * Internal standard: Decimal Degrees (DD)
 * User display: Configurable (DD, DMS, DDM, MGRS, UTM)
 */

import { forward as mgrsForward, toPoint as mgrsToPoint } from 'mgrs'

export type CoordinateFormat = 'DD' | 'DMS' | 'DDM' | 'MGRS' | 'UTM'
export type UnitSystem = 'metric' | 'imperial'

export type MgrsPrecision = 1 | 2 | 3 | 4 | 5

export interface CoordinatePrecision {
  ddDecimals?: number | undefined
  dmsSecondDecimals?: number | undefined
  directionFormat?: 'signed' | 'cardinal' | undefined
  mgrsPrecision?: MgrsPrecision | undefined
  utmShowZone?: boolean | undefined
}

export interface Position {
  lng: number
  lat: number
  elevation?: number | undefined
  timestamp?: number | undefined
}

export interface UserPreferences {
  coordinateFormat: CoordinateFormat
  unitSystem: UnitSystem
  elevationUnit: 'meters' | 'feet'
  distanceUnit: 'kilometers' | 'miles'
  areaUnit: 'hectares' | 'acres' | 'square_kilometers' | 'square_miles'
  temperatureUnit: 'celsius' | 'fahrenheit'
}

function llToUTM(
  lat: number,
  lng: number
): { zoneNumber: number; zoneLetter: string; easting: number; northing: number } {
  const a = 6378137.0
  const eccSquared = 0.00669438
  const k0 = 0.9996

  let zoneNumber = Math.floor((lng + 180) / 6) + 1
  if (lng === 180) {
    zoneNumber = 60
  }
  if (lat >= 56.0 && lat < 64.0 && lng >= 3.0 && lng < 12.0) {
    zoneNumber = 32
  }
  if (lat >= 72.0 && lat < 84.0) {
    if (lng >= 0.0 && lng < 9.0) {
      zoneNumber = 31
    } else if (lng >= 9.0 && lng < 21.0) {
      zoneNumber = 33
    } else if (lng >= 21.0 && lng < 33.0) {
      zoneNumber = 35
    } else if (lng >= 33.0 && lng < 42.0) {
      zoneNumber = 37
    }
  }

  const latRad = (lat * Math.PI) / 180
  const longOrigin = (zoneNumber - 1) * 6 - 180 + 3
  const longOriginRad = (longOrigin * Math.PI) / 180
  const eccPrimeSquared = eccSquared / (1 - eccSquared)
  const N = a / Math.sqrt(1 - eccSquared * Math.sin(latRad) * Math.sin(latRad))
  const T = Math.tan(latRad) * Math.tan(latRad)
  const C = eccPrimeSquared * Math.cos(latRad) * Math.cos(latRad)
  // const A = Math.cos(latRad) * (lat * Math.PI / 180 !== latRad ? 0 : (lng * Math.PI / 180 - longOriginRad))
  const AFixed = Math.cos(latRad) * ((lng * Math.PI) / 180 - longOriginRad)
  const M =
    a *
    ((1 - eccSquared / 4 - (3 * eccSquared * eccSquared) / 64 - (5 * eccSquared ** 3) / 256) *
      latRad -
      ((3 * eccSquared) / 8 + (3 * eccSquared * eccSquared) / 32 + (45 * eccSquared ** 3) / 1024) *
        Math.sin(2 * latRad) +
      ((15 * eccSquared * eccSquared) / 256 + (45 * eccSquared ** 3) / 1024) *
        Math.sin(4 * latRad) -
      ((35 * eccSquared ** 3) / 3072) * Math.sin(6 * latRad))

  const easting =
    k0 *
      N *
      (AFixed +
        ((1 - T + C) * AFixed ** 3) / 6 +
        ((5 - 18 * T + T * T + 72 * C - 58 * eccPrimeSquared) * AFixed ** 5) / 120) +
    500000.0
  let northing =
    k0 *
    (M +
      N *
        Math.tan(latRad) *
        ((AFixed * AFixed) / 2 +
          ((5 - T + 9 * C + 4 * C * C) * AFixed ** 4) / 24 +
          ((61 - 58 * T + T * T + 600 * C - 330 * eccPrimeSquared) * AFixed ** 6) / 720))
  if (lat < 0) {
    northing += 10000000.0
  }

  const letters = 'CDEFGHJKLMNPQRSTUVWXX'
  const letterIndex = Math.floor((lat + 80) / 8)
  const zoneLetter = letters[Math.min(Math.max(letterIndex, 0), 20)]

  return { zoneNumber, zoneLetter, easting: Math.round(easting), northing: Math.round(northing) }
}

/**
 * Convert decimal degrees to Degrees, Minutes, Seconds
 */
export function toDMS(decimalDegrees: number): {
  degrees: number
  minutes: number
  seconds: number
} {
  const absolute = Math.abs(decimalDegrees)
  const degrees = Math.floor(absolute)
  const minutesFloat = (absolute - degrees) * 60
  const minutes = Math.floor(minutesFloat)
  const seconds = (minutesFloat - minutes) * 60

  return { degrees, minutes, seconds }
}

/**
 * Convert decimal degrees to Degrees, Decimal Minutes
 */
export function toDDM(decimalDegrees: number): { degrees: number; minutes: number } {
  const absolute = Math.abs(decimalDegrees)
  const degrees = Math.floor(absolute)
  const minutes = (absolute - degrees) * 60

  return { degrees, minutes }
}

export function formatPosition(
  position: Position,
  format: CoordinateFormat,
  precision?: CoordinatePrecision
): string {
  const { lat, lng } = position

  switch (format) {
    case 'DD': {
      const dec = precision?.ddDecimals ?? 5
      if (precision?.directionFormat === 'cardinal') {
        const latDir = lat >= 0 ? 'N' : 'S'
        const lngDir = lng >= 0 ? 'E' : 'W'
        return `${Math.abs(lat).toFixed(dec)}° ${latDir}, ${Math.abs(lng).toFixed(dec)}° ${lngDir}`
      }
      return `${lat.toFixed(dec)}°, ${lng.toFixed(dec)}°`
    }

    case 'DMS': {
      const secDec = precision?.dmsSecondDecimals ?? 0
      const latDMS = toDMS(lat)
      const lngDMS = toDMS(lng)
      if (precision?.directionFormat !== 'signed') {
        const latDir = lat >= 0 ? 'N' : 'S'
        const lngDir = lng >= 0 ? 'E' : 'W'
        return (
          `${latDMS.degrees}°${latDMS.minutes}'${latDMS.seconds.toFixed(secDec)}"${latDir}, ` +
          `${lngDMS.degrees}°${lngDMS.minutes}'${lngDMS.seconds.toFixed(secDec)}"${lngDir}`
        )
      }
      const latSign = lat < 0 ? '-' : ''
      const lngSign = lng < 0 ? '-' : ''
      return (
        `${latSign}${latDMS.degrees}°${latDMS.minutes}'${latDMS.seconds.toFixed(secDec)}", ` +
        `${lngSign}${lngDMS.degrees}°${lngDMS.minutes}'${lngDMS.seconds.toFixed(secDec)}"`
      )
    }

    case 'DDM': {
      const latDDM = toDDM(lat)
      const lngDDM = toDDM(lng)
      const latDirDDM = lat >= 0 ? 'N' : 'S'
      const lngDirDDM = lng >= 0 ? 'E' : 'W'
      return (
        `${latDDM.degrees}°${latDDM.minutes.toFixed(3)}'${latDirDDM}, ` +
        `${lngDDM.degrees}°${lngDDM.minutes.toFixed(3)}'${lngDirDDM}`
      )
    }

    case 'MGRS': {
      try {
        const acc = precision?.mgrsPrecision ?? 5
        return mgrsForward([lng, lat], acc)
      } catch {
        return `${lat.toFixed(6)}°, ${lng.toFixed(6)}°`
      }
    }

    case 'UTM': {
      try {
        const utm = llToUTM(lat, lng)
        const showZone = precision?.utmShowZone !== false
        if (showZone) {
          return `${utm.zoneNumber}${utm.zoneLetter} ${utm.easting}E ${utm.northing}N`
        }
        return `${utm.easting}E ${utm.northing}N`
      } catch {
        return `${lat.toFixed(6)}°, ${lng.toFixed(6)}°`
      }
    }

    default:
      return formatPosition(position, 'DD', precision)
  }
}

export function parseCoordinates(input: string): Position | null {
  const clean = input.trim().replace(/\s+/g, ' ')

  const ddMatch = clean.match(/^(-?\d+\.?\d*)[°,\s]+(-?\d+\.?\d*)°?$/)
  if (ddMatch) {
    return {
      lat: parseFloat(ddMatch[1]),
      lng: parseFloat(ddMatch[2]),
    }
  }

  const dmsMatch = clean.match(/^(\d+)°(\d+)'([\d.]+)"([NS])[,\s]+(\d+)°(\d+)'([\d.]+)"([EW])$/i)
  if (dmsMatch) {
    const [, latDeg, latMin, latSec, latDir, lngDeg, lngMin, lngSec, lngDir] = dmsMatch
    let lat = parseInt(latDeg) + parseInt(latMin) / 60 + parseFloat(latSec) / 3600
    let lng = parseInt(lngDeg) + parseInt(lngMin) / 60 + parseFloat(lngSec) / 3600
    if (latDir.toLowerCase() === 's') {
      lat = -lat
    }
    if (lngDir.toLowerCase() === 'w') {
      lng = -lng
    }
    return { lat, lng }
  }

  const ddmMatch = clean.match(/^(\d+)°([\d.]+)'([NS])[,\s]+(\d+)°([\d.]+)'([EW])$/i)
  if (ddmMatch) {
    const [, latDeg, latMin, latDir, lngDeg, lngMin, lngDir] = ddmMatch
    let lat = parseInt(latDeg) + parseFloat(latMin) / 60
    let lng = parseInt(lngDeg) + parseFloat(lngMin) / 60
    if (latDir.toLowerCase() === 's') {
      lat = -lat
    }
    if (lngDir.toLowerCase() === 'w') {
      lng = -lng
    }
    return { lat, lng }
  }

  const mgrsMatch = clean.match(/^\d{1,2}[C-X][A-Z]{2}\s*\d+\s*\d+$/i)
  if (mgrsMatch) {
    try {
      const [lng, lat] = mgrsToPoint(clean)
      return { lat, lng }
    } catch {
      return null
    }
  }

  const utmMatch = clean.match(/^(\d{1,2})\s*([C-X])\s+(\d+\.?\d*)\s*E?\s+(\d+\.?\d*)\s*N?$/i)
  if (utmMatch) {
    try {
      const [, zone, letter, easting, northing] = utmMatch
      const mgrsStr = mgrsForward([0, 0], 5)
      void mgrsStr
      const zoneNum = parseInt(zone)
      const e = parseFloat(easting)
      const n = parseFloat(northing)
      const hemisphere = letter.toUpperCase() >= 'N' ? 'N' : 'S'

      const a = 6378137.0
      const eccSquared = 0.00669438
      const k0 = 0.9996
      const e1 = (1 - Math.sqrt(1 - eccSquared)) / (1 + Math.sqrt(1 - eccSquared))
      const x = e - 500000.0
      let y = n
      if (hemisphere === 'S') {
        y -= 10000000.0
      }

      const longOrigin = (zoneNum - 1) * 6 - 180 + 3
      const eccPrimeSquared = eccSquared / (1 - eccSquared)
      const M = y / k0
      const mu =
        M / (a * (1 - eccSquared / 4 - (3 * eccSquared ** 2) / 64 - (5 * eccSquared ** 3) / 256))

      const phi1Rad =
        mu +
        ((3 * e1) / 2 - (27 * e1 ** 3) / 32) * Math.sin(2 * mu) +
        ((21 * e1 ** 2) / 16 - (55 * e1 ** 4) / 32) * Math.sin(4 * mu) +
        ((151 * e1 ** 3) / 96) * Math.sin(6 * mu)
      const N1 = a / Math.sqrt(1 - eccSquared * Math.sin(phi1Rad) ** 2)
      const T1 = Math.tan(phi1Rad) ** 2
      const C1 = eccPrimeSquared * Math.cos(phi1Rad) ** 2
      const R1 = (a * (1 - eccSquared)) / (1 - eccSquared * Math.sin(phi1Rad) ** 2) ** 1.5
      const D = x / (N1 * k0)

      let lat =
        phi1Rad -
        ((N1 * Math.tan(phi1Rad)) / R1) *
          ((D * D) / 2 -
            ((5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * eccPrimeSquared) * D ** 4) / 24 +
            ((61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * eccPrimeSquared - 3 * C1 * C1) *
              D ** 6) /
              720)
      lat = (lat * 180) / Math.PI

      let lng =
        (D -
          ((1 + 2 * T1 + C1) * D ** 3) / 6 +
          ((5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * eccPrimeSquared + 24 * T1 * T1) * D ** 5) /
            120) /
        Math.cos(phi1Rad)
      lng = longOrigin + (lng * 180) / Math.PI

      return { lat, lng }
    } catch {
      return null
    }
  }

  return null
}

/**
 * Convert distance based on unit system
 */
export function formatDistance(meters: number, preferences: UserPreferences): string {
  if (preferences.unitSystem === 'imperial') {
    if (preferences.distanceUnit === 'miles') {
      const miles = meters / 1609.34
      if (miles < 0.1) {
        const feet = meters * 3.28084
        return `${feet.toFixed(0)} ft`
      }
      return `${miles.toFixed(2)} mi`
    }
  }

  // Metric default
  if (meters < 1000) {
    return `${meters.toFixed(0)} m`
  }
  const km = meters / 1000
  return `${km.toFixed(2)} km`
}

/**
 * Convert elevation based on unit system
 */
export function formatElevation(meters: number, preferences: UserPreferences): string {
  if (preferences.elevationUnit === 'feet') {
    const feet = meters * 3.28084
    return `${feet.toFixed(0)} ft`
  }
  return `${meters.toFixed(0)} m`
}

/**
 * Convert area based on unit system
 */
export function formatArea(squareMeters: number, preferences: UserPreferences): string {
  if (preferences.unitSystem === 'imperial') {
    if (preferences.areaUnit === 'acres') {
      const acres = squareMeters / 4046.86
      return `${acres.toFixed(2)} acres`
    }
    if (preferences.areaUnit === 'square_miles') {
      const sqMiles = squareMeters / 2589988.11
      return `${sqMiles.toFixed(2)} sq mi`
    }
  }

  // Metric default
  if (preferences.areaUnit === 'hectares') {
    const hectares = squareMeters / 10000
    return `${hectares.toFixed(2)} ha`
  }

  const sqKm = squareMeters / 1000000
  return `${sqKm.toFixed(2)} sq km`
}

/**
 * Convert temperature based on unit system
 */
export function formatTemperature(celsius: number, preferences: UserPreferences): string {
  if (preferences.temperatureUnit === 'fahrenheit') {
    const fahrenheit = (celsius * 9) / 5 + 32
    return `${fahrenheit.toFixed(1)}°F`
  }
  return `${celsius.toFixed(1)}°C`
}

/**
 * Validate coordinates are within valid ranges
 */
export function isValidPosition(position: Position): boolean {
  return position.lat >= -90 && position.lat <= 90 && position.lng >= -180 && position.lng <= 180
}

/**
 * Default user preferences for new users
 */
export const DEFAULT_PREFERENCES: UserPreferences = {
  coordinateFormat: 'DD',
  unitSystem: 'metric',
  elevationUnit: 'meters',
  distanceUnit: 'kilometers',
  areaUnit: 'hectares',
  temperatureUnit: 'celsius',
}
